"use client";

import type * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import type {
	ControllerProps,
	FieldPath,
	FieldValues
} from "react-hook-form";
import {
	Controller,
	FormProvider,
	useFormContext,
} from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { Label } from "~/components/label";
import { cn } from "~/lib/utils";

import { Button } from "./button";
import { CardIndicator } from "./card";

const Form = FormProvider;

interface FormFieldContextValue<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
	name: TName;
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
	{} as FormFieldContextValue
);

function FormField<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
	...props
}: ControllerProps<TFieldValues, TName>) {
	return (
		<FormFieldContext value={{ name: props.name }}>
			{/* https://github.com/orgs/react-hook-form/discussions/10964#discussioncomment-11950047 */}
			<Controller {...props} disabled={props.disabled || undefined} />
		</FormFieldContext>
	);
}

function useFormField() {
	const fieldContext = React.useContext(FormFieldContext);
	const itemContext = React.useContext(FormItemContext);
	const { getFieldState, formState } = useFormContext();

	const fieldState = getFieldState(fieldContext.name, formState);

	if (!fieldContext) {
		throw new Error("useFormField should be used within <FormField>");
	}

	const { id } = itemContext;

	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		...fieldState,
	};
}

interface FormItemContextValue {
	id: string;
}

const FormItemContext = React.createContext<FormItemContextValue>(
	{} as FormItemContextValue
);

function FormItem({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) {
	const id = React.useId();

	return (
		<FormItemContext value={{ id }}>
			<div className={cn("space-y-2", className)} ref={reference} {...props} />
		</FormItemContext>
	);
}
FormItem.displayName = "FormItem";

function FormLabel({ ref: reference, className, ...props }: { ref?: React.Ref<React.ElementRef<typeof LabelPrimitive.Root>> } & React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>) {
	const { error, formItemId } = useFormField();

	return (
		<Label
			className={cn(error && "text-destructive", className)}
			htmlFor={formItemId}
			ref={reference}
			{...props}
		/>
	);
}
FormLabel.displayName = "FormLabel";

function FormControl({ ref: reference, ...props }: { ref?: React.Ref<React.ElementRef<typeof Slot>> } & React.ComponentPropsWithoutRef<typeof Slot>) {
	const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

	return (
		<Slot
			aria-describedby={
				!error
					? `${formDescriptionId}`
					: `${formDescriptionId} ${formMessageId}`
			}
			aria-invalid={!!error}
			id={formItemId}
			ref={reference}
			{...props}
		/>
	);
}
FormControl.displayName = "FormControl";

function FormDescription({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLParagraphElement> } & React.HTMLAttributes<HTMLParagraphElement>) {
	const { formDescriptionId } = useFormField();

	return (
		<p
			className={cn("text-sm text-muted-foreground", className)}
			id={formDescriptionId}
			ref={reference}
			{...props}
		/>
	);
}
FormDescription.displayName = "FormDescription";

function FormMessage({ ref: reference, className, children, ...props }: { ref?: React.Ref<HTMLParagraphElement> } & React.HTMLAttributes<HTMLParagraphElement>) {
	const { error, formMessageId } = useFormField();
	const body = error ? String(error?.message) : children;

	if (!body || body === "undefined") return null;

	return (
		<p
			className={cn("text-sm font-medium text-destructive", className)}
			id={formMessageId}
			ref={reference}
			{...props}
		>
			{body}
		</p>
	);
}
FormMessage.displayName = "FormMessage";

function FormButton({ ref: reference, disabled: _disabled, children, ...props }: { ref?: React.Ref<HTMLButtonElement> } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
	const { formState: { disabled: formDisabled, isLoading, isSubmitting, isDirty } } = useFormContext();

	const pending = isLoading || isSubmitting;
	const disabled = _disabled || formDisabled || !isDirty;

	return (
		<Button
			disabled={disabled}
			pending={pending}
			ref={reference}
			type="submit"
			variant={isDirty ? "default" : "secondary"}
			{...props}
		>
			{children}
		</Button>
	);
}
FormButton.displayName = "FormButton";

export const UnsavedChangesIndicator: React.FC<{ className?: string }> = ({ className }) => (
	<CardIndicator className={className}>
		Unsaved changes
	</CardIndicator>
);

export const IncompleteIndicator: React.FC<{ className?: string }> = ({ className }) => (
	<CardIndicator className={twMerge("from-red-500 to-red-600", className)}>
		Incomplete
	</CardIndicator>
);

export const FormIndicator: React.FC<{ className?: string }> = ({ className }) => {
	const { formState: { disabled, isValid, isDirty } } = useFormContext();

	if (isDirty) return <UnsavedChangesIndicator className={className} />;
	if (!isValid && !disabled) return <IncompleteIndicator className={className} />;

	return null;
};

export {
	Form,
	FormButton,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	// eslint-disable-next-line react-refresh/only-export-components
	useFormField,
};
